import { bookingsResponse } from '@nono/contract';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api, getAuthToken } from './api';
import { useFilters } from './filters';

/*
 * Typed query hooks — one per dashboard endpoint, keyed on the active filters.
 * Every response is Zod-validated inside the client, so these return fully
 * typed data straight from @nono/contract. Query keys include the filter inputs
 * so TanStack Query caches per (endpoint, from, to, segment).
 */

export function useMetrics() {
  const { from, to, segment } = useFilters();
  return useQuery({
    queryKey: ['metrics', { from, to, segment }],
    queryFn: () => api.metrics({ from, to, segment }),
  });
}

export function useFunnel() {
  const { from, to, segment } = useFilters();
  return useQuery({
    queryKey: ['funnel', { from, to, segment }],
    queryFn: () => api.funnel({ from, to, segment }),
  });
}

export function useAds(level: 'ad' | 'campaign') {
  const { from, to, segment } = useFilters();
  return useQuery({
    queryKey: ['ads', { from, to, segment, level }],
    queryFn: () => api.ads({ from, to, segment, level }),
  });
}

export function usePlanRates(lookback?: number) {
  return useQuery({
    queryKey: ['plan-rates', { lookback: lookback ?? null }],
    queryFn: () => api.planRates(lookback),
  });
}

/**
 * Landing intelligence (v2). `handle` selects one landing page ('provetime',
 * 'lege-anbefalt', …) or 'alle' for the combined view — it is part of the
 * query key so each LP caches independently per date range.
 */
export function useLanding(handle: string) {
  const { from, to } = useFilters();
  return useQuery({
    queryKey: ['landing', { from, to, handle }],
    queryFn: () => api.landing({ from, to, handle }),
  });
}

/**
 * Named bookings (Momence sale lines) for a range. The pinned contract ships
 * the bookings schemas but no client method yet, so this fetches the endpoint
 * directly with the exact same semantics as the generated client (same-origin
 * cookie + Bearer token, Zod-parsed response, ApiError on non-2xx).
 */
async function fetchBookings(from: string, to: string) {
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const params = new URLSearchParams({ from, to });
  const res = await fetch(`/api/dashboard/bookings?${params}`, {
    headers,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { error?: unknown };
      if (typeof j.error === 'string') msg = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg);
  }
  return bookingsResponse.parse(await res.json());
}

/**
 * Named bookings drilldown, newest first. Keyed on (from, to) so Overview's
 * KPI box and the per-ad lists on the Ads page share one cache entry. Fresher
 * staleTime than the default: bookings land on the 2-min Momence sync.
 */
export function useBookings(from: string, to: string) {
  return useQuery({
    queryKey: ['bookings', { from, to }],
    queryFn: () => fetchBookings(from, to),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTargets() {
  return useQuery({ queryKey: ['targets'], queryFn: () => api.targets() });
}

export function useRules() {
  return useQuery({ queryKey: ['rules'], queryFn: () => api.rules() });
}

export function useAlerts() {
  return useQuery({ queryKey: ['alerts'], queryFn: () => api.alerts() });
}

/** When daily_metrics was last recomputed (drives the "data as of" stamp). */
export function useFreshness() {
  return useQuery({
    queryKey: ['freshness'],
    queryFn: () => api.freshness(),
    refetchInterval: 60_000, // keep the stamp current as the 30-min cron refreshes
    staleTime: 0,
  });
}
